import React, { useEffect, useState } from "react";
import { Card, Avatar, Button, Pagination, Spin, App, Select } from "antd";
import { apiGetPlans, apiUpdatePlan } from "../../api/plans";

const { Option } = Select;

const Plans = () => {
  const { message } = App.useApp();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    perPage: 10,
    totalPages: 1,
    nextPage: null,
  });

  const [sendingId, setSendingId] = useState(null);

  const planOptions = [
    { value: 1, label: "Tiêu chuẩn" },
    { value: 2, label: "Phần thưởng" },
    { value: 3, label: "Trả lại" },
  ];

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await apiGetPlans({
        page: currentPage,
        perPage: meta.perPage,
      });

      const raw = response.data || [];
      const mapped = raw.map((p) => ({
        id: p.id,
        patientId: p.patient?.id,
        patientName: p.patient?.fullName || "Không rõ",
        patientImage: p.patient?.profileImage || null,
        planId: Number(p.plan),
        status: Boolean(p.status), // boolean: true => already sent
        discountAmount: p.discountAmount,
      }));

      setItems(mapped);
      if (response.meta) setMeta(response.meta);
    } catch (err) {
      console.error("Error fetching plans", err);
      message.error("Không thể tải kế hoạch");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePlanSelectChange = (newPlanValue, item) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === item.id ? { ...it, planId: Number(newPlanValue) } : it
      )
    );
  };

  // When user clicks "Send link" we call update API with { id, plan }
  const handleSendLink = async (item) => {
    // If already sending or already sent, no-op
    if (sendingId || item.status) return;

    try {
      setSendingId(item.id);
      const payload = { id: item.id, plan: Number(item.planId) };
      await apiUpdatePlan(payload);

      // on success mark status true and keep planId (server authoritative response could also be used)
      setItems((prev) =>
        prev.map((it) => (it.id === item.id ? { ...it, status: true } : it))
      );

      message.success(`Đã gửi liên kết đến ${item.patientName}`);
    } catch (err) {
      console.error("Failed to send link/update plan", err);
      const errMsg = err?.message || err?.error || "Không thể gửi liên kết";
      message.error(errMsg);
    } finally {
      setSendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Kế hoạch</h1>
        <p className="text-gray-500 mt-1">
          Xem và quản lý lịch trình và kế hoạch điều trị cho bệnh nhân của bạn
        </p>
      </div>

      <Card
        title={
          <span className="text-lg font-semibold text-gray-900">
            Quản lý kế hoạch
          </span>
        }
        className="shadow-sm rounded-lg"
      >
        {items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Không tìm thấy kế hoạch nào</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg">
              <thead className="bg-purple-50">
                <tr>
                  <th className="text-left px-6 py-4">Bệnh nhân</th>
                  <th className="text-left px-6 py-4">Kế hoạch</th>
                  <th className="text-left px-6 py-4">Trạng thái</th>
                  <th className="text-left px-6 py-4">Hành động</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const disabledControls = Boolean(item.status);
                  return (
                    <tr key={item.id} className="border-t">
                      <td className="px-6 py-4 flex items-center gap-4">
                        <Avatar
                          size={36}
                          src={item.patientImage}
                          style={{
                            backgroundColor: item.patientImage
                              ? undefined
                              : "#8B5CF6",
                          }}
                        >
                          {!item.patientImage &&
                            item.patientName?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <div>
                          <div className="font-medium text-gray-800">
                            {item.patientName}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <Select
                            value={item.planId}
                            onChange={(val) =>
                              handlePlanSelectChange(val, item)
                            }
                            disabled={disabledControls}
                            className="w-64"
                          >
                            {planOptions.map((opt) => (
                              <Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 text-sm rounded-full ${
                            item.status
                              ? "bg-blue-50 text-blue-600" // sent — blue
                              : "bg-green-50 text-green-600" // not sent — green
                          }`}
                        >
                          {item.status
                            ? "Sent link to patient"
                            : "Send link to patient"}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Button
                            type="primary"
                            className={`${
                              disabledControls
                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                : "bg-purple-600 hover:bg-purple-700 border-purple-600"
                            }`}
                            onClick={() => handleSendLink(item)}
                            disabled={disabledControls || sendingId === item.id}
                          >
                            {sendingId === item.id ? (
                              <Spin size="small" />
                            ) : (
                              "Gửi liên kết"
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {items.length > 0 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              Đang hiển thị {(meta.currentPage - 1) * meta.perPage + 1}-
              {Math.min(
                meta.currentPage * meta.perPage,
                meta.total
              )}{" "}
              của {meta.total} kế hoạch
            </span>
            <Pagination
              current={meta.currentPage}
              total={meta.total}
              pageSize={meta.perPage}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default Plans;
